import { Box } from "@mui/system";
import { useEffect, useState } from "react";
import Icon from "../../../../../../UI/Icon";
import {
    ColorInput,
    FontSelect,
    FontSize,
    LetterSpacing,
    LineHeight
} from "../../../../../../UI/CommonComponents";
import { usePrinterContext } from "../../../../../../../context/printer";
import { Fonts } from "../../../../../../../const/font";
import { cloneDeep, isEqual } from "lodash";
import CustomTooltip from "../../../../../../UI/CustomTooltip/CustomTooltip";

export default function ItemsPrices({ data }) {
    const {
        pageBlocks,
        setPageBlocks,
        sectionBlock,
        setSectionBlock,
        undoRedoState,
        pageBlocksIndex,
        pageBlocksArray
    } = usePrinterContext();
    const [dependencyChanged, setDependencyChanged] = useState(false);

    const updatedPageBlocks = [...pageBlocks];
    const blockToUpdate =
        updatedPageBlocks[data.pageIndex]?.row[data?.rowIndex]?.column[
            data.columnIndex
        ]?.blocks[data.blockIndex]?.data?.styles?.item;

    const selectedFontName = Fonts.find(
        (font) => font.name === blockToUpdate?.itemPrice?.fontFamily
    );

    const [lineHeight, setLineHeight] = useState(
        blockToUpdate?.itemPrice?.lineHeight
    );
    const [fontSize, setFontSize] = useState(
        blockToUpdate?.itemPrice?.fontSize
    );
    const [letterSpacing, setLetterSpacing] = useState(
        blockToUpdate?.itemPrice?.letterSpacing
    );
    const [upperCaseText, setUpperCaseText] = useState(
        blockToUpdate?.itemPrice?.upperCase
    );
    const [textColor, setTextColor] = useState(blockToUpdate?.itemPrice?.color);
    const [boldFont, setBoldFont] = useState(
        blockToUpdate?.itemPrice?.boldFont === "bold" ? true : false
    );
    const [italicFont, setItalicFont] = useState(
        blockToUpdate?.itemPrice?.fontStyle === "normal" ? false : true
    );
    const [selectedFont, setSelectedFont] = useState(selectedFontName);
    const [selectedFontWeight, setSelectedFontWeight] = useState(
        blockToUpdate?.itemPrice?.fontWeight
    );

    useEffect(() => {
        if (undoRedoState?.state && isEqual(data, sectionBlock.data)) {
            return;
        }

        setLineHeight(blockToUpdate?.itemPrice?.lineHeight);
        setFontSize(blockToUpdate?.itemPrice?.fontSize);
        setLetterSpacing(blockToUpdate?.itemPrice?.letterSpacing);
        setUpperCaseText(blockToUpdate?.itemPrice?.upperCase);
        setTextColor(blockToUpdate?.itemPrice?.color);
        setSelectedFont(selectedFontName);
        setSelectedFontWeight(blockToUpdate?.itemPrice?.fontWeight);
        setBoldFont(
            blockToUpdate?.itemPrice?.boldFont === "bold" ? true : false
        );
        setItalicFont(
            blockToUpdate?.itemPrice?.fontStyle === "normal" ? false : true
        );

        setSectionBlock({ data: data, pageBlocks: pageBlocks });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.pageIndex, data.columnIndex, data.blockIndex, undoRedoState]);

    useEffect(() => {
        const clonedPageBlocksArray = cloneDeep(pageBlocksArray);
        const historyBlocks = clonedPageBlocksArray[pageBlocksIndex.current];

        if (isEqual(pageBlocks, historyBlocks) && !dependencyChanged) {
            return;
        }

        const newStyles = {
            lineHeight,
            fontSize,
            color: textColor,
            fontWeight: selectedFontWeight,
            boldFont: boldFont ? "bold" : "normal",
            fontStyle: italicFont ? "italic" : "normal",
            fontFamily: selectedFont?.name,
            letterSpacing,
            upperCase: upperCaseText
        };

        const updatedPageBlocks = [...pageBlocks];
        const blockToUpdate =
            updatedPageBlocks[data.pageIndex]?.row[data?.rowIndex]?.column[
                data.columnIndex
            ]?.blocks[data.blockIndex]?.data?.styles?.item;
        const itemBlockToUpdate =
            updatedPageBlocks[data.pageIndex]?.row[data?.rowIndex]?.column[
                data.columnIndex
            ]?.blocks[data.blockIndex]?.data?.items?.items;

        if (blockToUpdate) {
            blockToUpdate.itemPrice = {
                ...blockToUpdate.itemPrice,
                ...newStyles
            };
            itemBlockToUpdate?.forEach((item) => {
                if (!item.styles.enabled) {
                    item.styles.itemPrice = {
                        ...item.styles.itemPrice,
                        ...newStyles
                    };
                }
            });
            // Update the state with the modified pageBlocks
            setPageBlocks(updatedPageBlocks);
        }
        setSectionBlock({
            pageBlocks: updatedPageBlocks
        });
        setDependencyChanged(false);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        upperCaseText,
        letterSpacing,
        lineHeight,
        fontSize,
        textColor,
        boldFont,
        italicFont,
        selectedFont,
        selectedFontWeight
    ]);

    return (
        <div className="collapseContentWrapper itemPrices">
            <Box className="fontFamilyOption">
                <Box className="fontLabel">Font</Box>
                <Box className="fontsSelect">
                    <FontSelect
                        selectedFont={selectedFont}
                        setSelectedFont={setSelectedFont}
                        selectedFontWeight={selectedFontWeight}
                        setSelectedFontWeight={setSelectedFontWeight}
                        setDependencyChanged={setDependencyChanged}
                    />
                    <Box className="fontSizeOption">
                        <LineHeight
                            setLineHeight={setLineHeight}
                            lineHeight={lineHeight}
                            setDependencyChanged={setDependencyChanged}
                        />
                        <FontSize
                            setFontSize={setFontSize}
                            fontSize={fontSize}
                            setDependencyChanged={setDependencyChanged}
                        />
                    </Box>
                    <LetterSpacing
                        setLetterSpacing={setLetterSpacing}
                        letterSpacing={letterSpacing}
                        setDependencyChanged={setDependencyChanged}
                    />
                </Box>
            </Box>
            <Box className="fontStylesOption flexBox alignCenter justifyBetween">
                <Box className="fontLabel">Text Styles</Box>
                <Box className="flexBox alignCenter">
                    {/* <Icon
                        className={`styleOption ${boldFont ? "active" : ""}`}
                        name="boldIcon"
                        onClick={() => {
                            setBoldFont(!boldFont);
                            setDependencyChanged(true);
                        }}
                    /> */}
                    <CustomTooltip title="Uppercase">
                        <Box
                            className={`styleOption ${
                                upperCaseText ? "active" : ""
                            }`}
                        >
                            <Icon
                                name="allCapsIcon"
                                onClick={() => {
                                    setUpperCaseText(!upperCaseText);
                                    setDependencyChanged(true);
                                }}
                            />
                        </Box>
                    </CustomTooltip>
                    <CustomTooltip title="Italic">
                        <Box
                            className={`styleOption ${
                                italicFont ? "active" : ""
                            }`}
                        >
                            <Icon
                                name="italicIcon"
                                onClick={() => {
                                    setItalicFont(!italicFont);
                                    setDependencyChanged(true);
                                }}
                            />
                        </Box>
                    </CustomTooltip>
                    <CustomTooltip title="Color">
                        <Box sx={{ ml: 1 }}>
                            <ColorInput
                                value={textColor}
                                setValue={setTextColor}
                                setDependencyChanged={setDependencyChanged}
                            />
                        </Box>
                    </CustomTooltip>
                </Box>
            </Box>
        </div>
    );
}
